import { AudioRecordingRecord } from "@/domains/dexie-db/models/audio-recording/audio-recording";
import { clientEnvModule } from "@/modules/client-env";
import { RecallPermissionKind, type RecallPermissionMap } from "@/modules/electron/types";
import {
  AudioRecordingAppEventKind,
  AudioRecordingStrategy,
  getAudioRecordingStrategy,
} from "@/store/audio-recordings/types";
import {
  MEETING_HANDOFF_PIPELINE_DEFERRED_PROPERTY,
  MEETING_HANDOFF_SUCCESSOR_AUDIO_RECORDING_ID_PROPERTY,
} from "@/store/audio-recordings/app-events";
import type { AudioRecordingArtifactDebugSummary } from "@/store/audio/recording/strategies/audio-recording-strategy-driver";
import type { AudioUploadFailureDebugInfo, AudioUploadFailureStage } from "@mem-client/uploads";
import type { Uuid } from "@/domains/global/identifiers";

const RECORDING_DURATION_MISMATCH_THRESHOLD_MS = 1_000;

export type RecordingCompletedArtifactSummary = AudioRecordingArtifactDebugSummary;
export type RecordingCompletedSilenceSummary = {
  hasEverDetectedSound: boolean;
  observedDurationMs: number;
};

type LatestAppEvent = {
  kind: AudioRecordingAppEventKind;
  occurred_at: string;
  properties?: Record<string, unknown>;
};

export type BuildRecordingCompletedAppEventPropertiesArgs = {
  audioRecording: AudioRecordingRecord | null;
  durationMs?: number | null;
  recordingStrategy?: AudioRecordingStrategy | null;
  transcriptTurnCount?: number | null;
  artifactSummary?: RecordingCompletedArtifactSummary;
  silenceSnapshot?: RecordingCompletedSilenceSummary | null;
  deferredPipelineSuccessorAudioRecordingId?: Uuid | null;
  clientGroupId: string;
  clientId: string;
  occurredAt: string;
};

export type BuildUploadErrorAppEventPropertiesArgs = {
  errorMessage?: string | null;
  uploadStage?: AudioUploadFailureStage;
  forceRestart?: boolean;
  debugInfo?: AudioUploadFailureDebugInfo;
  failureKind?: "artifact-missing" | "upload-failed";
};

export type BuildUploadStartAppEventPropertiesArgs = {
  audioRecording: AudioRecordingRecord | null;
  forceRestart?: boolean;
};

export function buildRecordingCompletedAppEventProperties({
  audioRecording,
  durationMs,
  recordingStrategy,
  transcriptTurnCount,
  artifactSummary,
  silenceSnapshot,
  deferredPipelineSuccessorAudioRecordingId,
  clientGroupId,
  clientId,
  occurredAt,
}: BuildRecordingCompletedAppEventPropertiesArgs): Record<string, unknown> {
  const startedEvent = findLatestAppEvent({
    audioRecording,
    kinds: [AudioRecordingAppEventKind.RESUMED_LOCAL, AudioRecordingAppEventKind.STARTED_LOCAL],
  });

  const derivedDurationMs = calculateDerivedDurationMs({
    startedAt: startedEvent?.occurred_at,
    completedAt: occurredAt,
  });

  const normalizedDurationMs = normalizeFiniteNumber({ value: durationMs });
  const normalizedArtifactDurationMs = normalizeFiniteNumber({
    value: artifactSummary?.durationMs,
  });

  const durationMismatchMs = calculateDurationMismatchMs({
    derivedDurationMs,
    durationForMismatchMs: normalizedDurationMs ?? normalizedArtifactDurationMs,
  });

  return {
    duration_ms: normalizedDurationMs,
    derived_duration_ms: derivedDurationMs,
    duration_mismatch_ms: durationMismatchMs,
    has_duration_mismatch: exceedsDurationMismatchThreshold({ durationMismatchMs }),
    recording_strategy: resolveRecordingStrategy({ recordingStrategy, audioRecording }),
    transcript_turn_count: normalizeFiniteNumber({ value: transcriptTurnCount }),
    recall_sdk_upload_id: extractRecallSdkUploadId({ audioRecording }),
    client_group_id: clientGroupId,
    client_id: clientId,
    client_version: clientEnvModule.packageVersion(),
    artifact_duration_ms: normalizedArtifactDurationMs,
    artifact_part_count: normalizeFiniteNumber({ value: artifactSummary?.partCount }),
    artifact_size_bytes: normalizeFiniteNumber({ value: artifactSummary?.sizeBytes }),
    artifact_state: artifactSummary?.state,
    artifact_file_path: artifactSummary?.filePath,
    artifact_append_count: normalizeFiniteNumber({ value: artifactSummary?.appendCount }),
    artifact_bytes_written: normalizeFiniteNumber({ value: artifactSummary?.bytesWritten }),
    artifact_created_at: artifactSummary?.createdAt,
    artifact_updated_at: artifactSummary?.updatedAt,
    artifact_first_append_at: artifactSummary?.firstAppendAt,
    artifact_last_append_at: artifactSummary?.lastAppendAt,
    artifact_finalized_at: artifactSummary?.finalizedAt,
    recall_sdk_state_at_start: artifactSummary?.recallSdkStateAtStart,
    ...buildRecallPermissionProperties({
      recallPermissionsAtStart: artifactSummary?.recallPermissionsAtStart,
    }),
    has_empty_artifact: isArtifactEmpty({ sizeBytes: artifactSummary?.sizeBytes }),
    silence_has_detected_sound: silenceSnapshot?.hasEverDetectedSound,
    silence_observed_duration_ms: normalizeFiniteNumber({
      value: silenceSnapshot?.observedDurationMs,
    }),
    [MEETING_HANDOFF_PIPELINE_DEFERRED_PROPERTY]:
      deferredPipelineSuccessorAudioRecordingId != null ? true : undefined,
    [MEETING_HANDOFF_SUCCESSOR_AUDIO_RECORDING_ID_PROPERTY]:
      deferredPipelineSuccessorAudioRecordingId ?? undefined,
  };
}

export function buildUploadStartAppEventProperties({
  audioRecording,
  forceRestart,
}: BuildUploadStartAppEventPropertiesArgs): Record<string, unknown> {
  const completedProperties = extractPersistedCompletionAppEventProperties({
    audioRecording,
  });
  const completedRecordingStrategy = completedProperties.recording_strategy;

  return compactNullish({
    force_restart: forceRestart,
    recording_strategy:
      typeof completedRecordingStrategy === "string"
        ? completedRecordingStrategy
        : resolveRecordingStrategy({ recordingStrategy: null, audioRecording }),
    artifact_size_bytes: normalizeFiniteNumber({
      value: completedProperties.artifact_size_bytes as number | null | undefined,
    }),
  });
}

export function buildUploadErrorAppEventProperties({
  errorMessage,
  uploadStage,
  forceRestart,
  debugInfo,
  failureKind,
}: BuildUploadErrorAppEventPropertiesArgs): Record<string, unknown> {
  return compactNullish({
    error_message: errorMessage,
    upload_stage: uploadStage,
    force_restart: forceRestart,
    upload_failure_kind: failureKind,
    artifact_failure_reason: debugInfo?.artifactFailureReason,
    artifact_file_path: debugInfo?.artifactFilePath,
    manifest_path: debugInfo?.manifestPath,
    artifact_directory_path: debugInfo?.artifactDirectoryPath,
    artifact_state: debugInfo?.artifactState,
    artifact_size_bytes: debugInfo?.artifactSizeBytes,
    artifact_append_count: debugInfo?.appendCount,
    artifact_append_failure_count: debugInfo?.appendFailureCount,
    artifact_bytes_written: debugInfo?.bytesWritten,
    artifact_created_at: debugInfo?.createdAt,
    artifact_updated_at: debugInfo?.updatedAt,
    artifact_first_append_at: debugInfo?.firstAppendAt,
    artifact_last_append_at: debugInfo?.lastAppendAt,
    artifact_finalized_at: debugInfo?.finalizedAt,
    artifact_ref_present: debugInfo?.artifactRefPresent,
    artifact_snapshot_present: debugInfo?.artifactSnapshotPresent,
    artifact_remove_reason: debugInfo?.removeReason,
    artifact_removed_at: debugInfo?.removedAt,
    diagnostic_error_message: debugInfo?.diagnosticErrorMessage,
  });
}

export function extractPersistedCompletionAppEventProperties({
  audioRecording,
}: {
  audioRecording: AudioRecordingRecord | null;
}): Record<string, unknown> {
  const completedEvent = findLatestAppEvent({
    audioRecording,
    kinds: [AudioRecordingAppEventKind.COMPLETED_LOCAL],
  });

  return completedEvent?.properties ?? {};
}

export function compactNullish(properties: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(properties).filter(([, value]) => value != null));
}

function normalizeFiniteNumber({
  value,
}: {
  value: number | null | undefined;
}): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return undefined;
  }

  return Math.round(value);
}

function calculateDerivedDurationMs({
  startedAt,
  completedAt,
}: {
  startedAt?: string;
  completedAt: string;
}): number | undefined {
  if (!startedAt) {
    return undefined;
  }

  const startedAtMs = Date.parse(startedAt);
  const completedAtMs = Date.parse(completedAt);

  if (!Number.isFinite(startedAtMs) || !Number.isFinite(completedAtMs)) {
    return undefined;
  }

  return Math.max(0, Math.round(completedAtMs - startedAtMs));
}

function calculateDurationMismatchMs({
  derivedDurationMs,
  durationForMismatchMs,
}: {
  derivedDurationMs: number | undefined;
  durationForMismatchMs: number | undefined;
}): number | undefined {
  if (derivedDurationMs === undefined || durationForMismatchMs === undefined) {
    return undefined;
  }

  return Math.abs(derivedDurationMs - durationForMismatchMs);
}

function exceedsDurationMismatchThreshold({
  durationMismatchMs,
}: {
  durationMismatchMs: number | undefined;
}): boolean | undefined {
  if (durationMismatchMs === undefined) {
    return undefined;
  }

  return durationMismatchMs >= RECORDING_DURATION_MISMATCH_THRESHOLD_MS;
}

function resolveRecordingStrategy({
  recordingStrategy,
  audioRecording,
}: {
  recordingStrategy?: AudioRecordingStrategy | null;
  audioRecording: AudioRecordingRecord | null;
}): AudioRecordingStrategy | undefined {
  if (recordingStrategy) {
    return recordingStrategy;
  }

  if (!audioRecording) {
    return undefined;
  }

  return getAudioRecordingStrategy({ modelData: audioRecording.data });
}

function isArtifactEmpty({ sizeBytes }: { sizeBytes: number | undefined }): boolean | undefined {
  if (sizeBytes === undefined) {
    return undefined;
  }

  return sizeBytes <= 0;
}

function findLatestAppEvent({
  audioRecording,
  kinds,
}: {
  audioRecording: AudioRecordingRecord | null;
  kinds: AudioRecordingAppEventKind[];
}): LatestAppEvent | undefined {
  const valueJson = audioRecording?.data.value_json as
    | {
        app_events?: Array<{
          kind?: unknown;
          occurred_at?: unknown;
          properties?: Record<string, unknown>;
        }>;
      }
    | undefined;
  const appEvents = valueJson?.app_events ?? [];

  for (let index = appEvents.length - 1; index >= 0; index -= 1) {
    const appEvent = appEvents[index];

    if (
      appEvent?.occurred_at &&
      typeof appEvent.occurred_at === "string" &&
      kinds.includes(appEvent.kind as AudioRecordingAppEventKind)
    ) {
      return {
        kind: appEvent.kind as AudioRecordingAppEventKind,
        occurred_at: appEvent.occurred_at,
        properties: appEvent.properties,
      };
    }
  }

  return undefined;
}

function extractRecallSdkUploadId({
  audioRecording,
}: {
  audioRecording: AudioRecordingRecord | null;
}): string | undefined {
  // A resume can be recorded without the upload id (a recovered desktop
  // session resumes without renderer-held credentials), so fall back to the
  // start event that captured it.
  const candidateKinds = [
    [AudioRecordingAppEventKind.RESUMED_LOCAL, AudioRecordingAppEventKind.STARTED_LOCAL],
    [AudioRecordingAppEventKind.STARTED_LOCAL],
  ];

  for (const kinds of candidateKinds) {
    const recallEvent = findLatestAppEvent({ audioRecording, kinds });
    const recallSdkUploadId = recallEvent?.properties?.recall_sdk_upload_id;
    if (typeof recallSdkUploadId === "string") return recallSdkUploadId;
  }

  return undefined;
}

function buildRecallPermissionProperties({
  recallPermissionsAtStart,
}: {
  recallPermissionsAtStart?: RecallPermissionMap;
}): Record<string, string | undefined> {
  return {
    recall_permission_accessibility_at_start:
      recallPermissionsAtStart?.[RecallPermissionKind.Accessibility],
    recall_permission_screen_capture_at_start:
      recallPermissionsAtStart?.[RecallPermissionKind.ScreenCapture],
    recall_permission_system_audio_at_start:
      recallPermissionsAtStart?.[RecallPermissionKind.SystemAudio],
    recall_permission_microphone_at_start:
      recallPermissionsAtStart?.[RecallPermissionKind.Microphone],
    recall_permission_full_disk_access_at_start:
      recallPermissionsAtStart?.[RecallPermissionKind.FullDiskAccess],
  };
}
